import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import ImageDescription1 from '@/assets/images/image-home-utilize-description-1.webp';
import ImageDescription2 from '@/assets/images/image-home-utilize-description-2.webp';
import ImageDescription3 from '@/assets/images/image-home-utilize-description-3.webp';
import ImageMain from '@/assets/images/image-home-utilize-main.png';
import Carousel from '@/components/Carousel';
import CoverImage, { Props as CoverImageProps } from '@/components/CoverImage';
import Divider from '@/components/Divider';
import Flex, { ColumnCenter, ColumnFlex } from '@/components/Flex';
import ImageWrapper from '@/components/ImageWrapper';
import SliderPage from '@/components/SliderPage';
import ButtonTabDialog from '@/components/dialog/ButtonTabDialog';
import { IconBtcusdLogo } from '@/components/icons';
import Card from '@/components/surface/Card';
import LandingTitle from '@/components/text/LandingTitle';
import StyledText from '@/components/text/StyledText';
import useDeviceSize from '@/hooks/mobile/useDeviceSize';
import SectionHomePage from './SectionHomePage';

const ContentInImage = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  padding: ${({ theme }) => theme.size.xs};
  ${({ theme }) => theme.rounded.md}
  background-color: #ffffffcc;
`;

function DescriptionBody({
  header,
  body,
}: {
  header?: React.ReactNode;
  body?: React.ReactNode;
}) {
  return (
    <ColumnFlex $gap="xs">
      <StyledText $fontSize="md" $fontWeight="bold" $color="active">
        {header}
      </StyledText>
      <StyledText
        $color="secondary"
        $fontSize="xs"
        $fontWeight="regular"
        $whiteSpace="pre-wrap"
      >
        {body}
      </StyledText>
    </ColumnFlex>
  );
}
function DescriptionImage({ ...props }: CoverImageProps) {
  // device size
  const deviceSize = useDeviceSize();

  return (
    <CoverImage
      unoptimized
      $width={deviceSize === 'mobile' ? '100%' : 224}
      $height={236}
      {...props}
    />
  );
}

function CarouselItemDescription() {
  // device size
  const deviceSize = useDeviceSize();

  // translate
  const { t } = useTranslation('home', {
    keyPrefix: 'utilize.page.description',
  });

  // state
  const [value, setValue] = useState(0);

  // memo
  const contents = useMemo(
    () =>
      t('contents', { returnObjects: true }) as {
        header?: string;
        body?: string;
      }[],
    [t]
  );
  const menus = useMemo(
    () => contents.map(({ header }) => header ?? ''),
    [contents]
  );
  const items = useMemo(
    () =>
      contents.map((content, contentIndex) => (
        <DescriptionBody {...content} key={contentIndex} />
      )),
    [contents]
  );
  const images = useMemo(
    () => [
      <DescriptionImage src={ImageDescription1} key={0} />,
      <DescriptionImage src={ImageDescription2} key={1} />,
      <DescriptionImage src={ImageDescription3} key={2} />,
    ],
    []
  );

  // callback
  const handleMenuChange = useCallback((index: number) => setValue(index), []);

  return (
    <Card
      $height={deviceSize === 'mobile' ? 504 : undefined}
      $padding="xl"
      $shadow="unset"
    >
      <Flex
        $height="100%"
        $gap="xl"
        $direction={deviceSize === 'mobile' ? 'column' : undefined}
      >
        <ColumnFlex $gap={40} $grow={1}>
          <ButtonTabDialog
            value={value}
            items={menus}
            onChange={handleMenuChange}
          />
          <ImageWrapper>
            <SliderPage initial={false} value={value} items={items} />
          </ImageWrapper>
        </ColumnFlex>

        <ImageWrapper
          $width={deviceSize === 'mobile' ? '100%' : 224}
          $height={240}
        >
          <SliderPage initial={false} value={value} items={images} />
        </ImageWrapper>
      </Flex>
    </Card>
  );
}

function CarouselItemMain() {
  // device size
  const deviceSize = useDeviceSize();

  // translate
  const { t } = useTranslation('home', { keyPrefix: 'utilize.page.main' });

  // memo
  const contents = useMemo(
    () =>
      t('contents', { returnObjects: true }) as {
        header?: string;
        body?: string;
      }[],
    [t]
  );

  return (
    <Card
      $height={deviceSize === 'mobile' ? 504 : undefined}
      $padding="xl"
      $shadow="unset"
    >
      <Flex
        $gap="xl"
        $direction={deviceSize === 'mobile' ? 'column' : undefined}
      >
        <CoverImage
          unoptimized
          $width={deviceSize === 'mobile' ? '100%' : 236}
          $height={240}
          src={ImageMain}
        >
          <ContentInImage>
            <ColumnFlex $gap="xxs" $width={186}>
              <IconBtcusdLogo size={124} />
              <StyledText
                $fontSize="xs"
                $fontWeight="medium"
                $color="blackText"
              >
                {t('title')}
              </StyledText>
            </ColumnFlex>
          </ContentInImage>
        </CoverImage>
        <ColumnFlex $justify="center" $gap="xl" $grow={1}>
          {contents.map((content, contentIndex) => (
            <>
              {contentIndex !== 0 ? (
                <ColumnFlex
                  $justify="center"
                  $height="xxs"
                  key={`divider-${contentIndex}`}
                >
                  <Divider $borderWidth={1} />
                </ColumnFlex>
              ) : (
                ''
              )}
              <ColumnFlex $gap="xs" key={contentIndex}>
                <StyledText $fontSize="md" $fontWeight="bold">
                  {content?.header}
                </StyledText>
                <StyledText
                  $fontSize="xs"
                  $fontWeight="regular"
                  $color="secondary"
                >
                  {content?.body}
                </StyledText>
              </ColumnFlex>
            </>
          ))}
        </ColumnFlex>
      </Flex>
    </Card>
  );
}

export default function UtilizeSection() {
  // device size
  const deviceSize = useDeviceSize();

  // translate
  const { t } = useTranslation('home', { keyPrefix: 'utilize' });

  // state
  const [value, setValue] = useState(0);

  // memo
  const contents = useMemo(
    () => [<CarouselItemMain key={0} />, <CarouselItemDescription key={1} />],
    []
  );

  // callback
  const handlePrevClick = useCallback(
    () => setValue((value) => (value <= 0 ? contents.length - 1 : value - 1)),
    [contents.length]
  );
  const handleNextClick = useCallback(
    () => setValue((value) => (value >= contents.length - 1 ? 0 : value + 1)),
    [contents.length]
  );

  return (
    <SectionHomePage>
      <ColumnCenter $gap="xl">
        <LandingTitle>{t('title')}</LandingTitle>
        <StyledText
          $fontSize="md"
          $fontWeight="regular"
          $color="secondary"
          $textAlign="center"
          $whiteSpace="pre-wrap"
        >
          {t('description')}
        </StyledText>
      </ColumnCenter>

      <Carousel
        width={deviceSize !== 'mobile' ? 640 : undefined}
        value={value}
        items={contents}
        onPrevClick={handlePrevClick}
        onNextClick={handleNextClick}
      />
    </SectionHomePage>
  );
}
